import { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { ProjectServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { useDebounce } from "$utils/customHooks";
import { GenericSelectProps } from "./GenericSelect";
import SearchSelect from "./SearchSelect";
import useAuth from "$hooks/useAuth";

type Props = {
  onChange: (projectId, project) => void;
  value: any;
  error?: boolean;
  multiple?: boolean;
  className?: any;
  required?: boolean;
  disabled?: boolean;
  useRouteProjectWarehouse?: boolean;
  renderOption?: (opt) => JSX.Element;
  companyId?: string;
  label?: string;
  placeholder?: string;
  valueKey?: string;
  errorText?: string;
  rowsPerPage?: number;
  minCharsToTrigerSearch?: number;
  onDataFetched?: (options: any[]) => void;
};

const getProjectLabel = (option) =>
  `${option?.name}${option?.code ? ` - [${option?.code}]` : ""}`;

const ProjectSelect = ({
  companyId,
  valueKey = "_id",
  rowsPerPage = 20,
  value,
  multiple,
  onChange,
  required,
  useRouteProjectWarehouse = false,
  minCharsToTrigerSearch,
  label = "Projet",
  onDataFetched,
  ...props
}: Props & Partial<GenericSelectProps>) => {
  // Input text value is debounced to prevent too many calls
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue);

  const [localOptions, setLocalOptions] = useState<any[]>([]);

  // Track if we're updating from the parent component
  const [internalUpdate, setInternalUpdate] = useState(false);

  // Get user information to access contributorId
  const { user } = useAuth();

  const labelToUse = label || (multiple ? "Projets" : "Projet");

  // If value changes externally (from parent), update the input value to match
  useEffect(() => {
    // Ensure localOptions is always treated as an array
    const safeLocalOptions = Array.isArray(localOptions) ? localOptions : [];

    if (!internalUpdate) {
      if (value && safeLocalOptions.length > 0) {
        const selectedOption = multiple
          ? safeLocalOptions.filter((opt) =>
              (Array.isArray(value) ? value : [value]).includes(opt[valueKey])
            )
          : safeLocalOptions.find((opt) => opt[valueKey] === value);

        if (selectedOption && !multiple) {
          setInputValue(getProjectLabel(selectedOption));
        }
      } else {
        setInputValue("");
      }
    }
  }, [value, localOptions, multiple, valueKey, internalUpdate]);

  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setInputValue("");
    }
  }, [companyId, value]);

  const filters = useMemo(() => {
    const safeLocalOptions = Array.isArray(localOptions) ? localOptions : [];

    const nextFilters = {
      ...(companyId && { companyId }),
      ...(!!debouncedInputValue && { nameCode: debouncedInputValue }),
      ...{ isActive: true, companyDisable: false, status: "in_progress" },
      ...(useRouteProjectWarehouse && {
        contributorCompanyId: user?.userInfo?.userCompany?._id,
      }),
      rowsPerPage,
      page: 1,
    };

    // Check if search matches a selected option to avoid searching for the label itself
    let isSearchStringEqualsAnOption = false;

    if (multiple && Array.isArray(value)) {
      isSearchStringEqualsAnOption = (value || []).some((id) => {
        const opt = safeLocalOptions.find((opt) => opt[valueKey] === id);
        return opt && getProjectLabel(opt) === debouncedInputValue;
      });
    } else {
      const foundOpt = safeLocalOptions.find((opt) => opt[valueKey] === value);
      isSearchStringEqualsAnOption =
        foundOpt && getProjectLabel(foundOpt) === debouncedInputValue;
    }

    // Prevents adding the search on name or code if the inputValue is the selected product label
    if (!multiple && !!debouncedInputValue && isSearchStringEqualsAnOption) {
      nextFilters.nameCode = undefined;
    }

    return nextFilters;
  }, [
    companyId,
    debouncedInputValue,
    rowsPerPage,
    multiple,
    value,
    localOptions,
    valueKey,
    useRouteProjectWarehouse,
    user?.userInfo?.userCompany?._id,
  ]);

  // Custom onChange handler that manages the internal state
  const handleChange = useCallback(
    (selectedValue, selectedOption) => {
      setInternalUpdate(true);

      try {
        // Update local display options
        if (selectedOption) {
          if (multiple) {
            setInputValue(""); // reset search on select
            setLocalOptions(
              Array.isArray(selectedOption) ? selectedOption : [selectedOption]
            );
          } else {
            setInputValue(getProjectLabel(selectedOption));
            setLocalOptions([selectedOption]);
          }
        } else {
          setLocalOptions([]);
          setInputValue("");
        }

        // Pass to parent component
        if (onChange) {
          onChange(selectedValue, selectedOption);
        }
      } finally {
        // Reset the internal update flag after the cycle completes
        setTimeout(() => {
          setInternalUpdate(false);
        }, 0);
      }
    },
    [multiple, onChange]
  );

  const safeSetLocalOptions = useCallback((options: any) => {
    const safeOptions = Array.isArray(options)
      ? options
      : options
        ? [options]
        : [];
    setLocalOptions(safeOptions);
  }, []);

  const handleDataFetched = useCallback(
    (options: any[]) => {
      if (onDataFetched) {
        onDataFetched(options);
      }

      // Sync local options with fresh data from server if needed
      // This prevents the "nameCode" loop by ensuring localOptions has the full data (including code)
      if (options && options.length > 0 && value) {
        const valueArray = Array.isArray(value) ? value : [value];
        const relevantOptions = options.filter((opt) =>
          valueArray.includes(opt[valueKey])
        );

        if (relevantOptions.length > 0) {
          setLocalOptions((prev) => {
            // Only update if there are actual changes to avoid infinite loop
            const hasChanges = relevantOptions.some((newOpt) => {
              const prevOpt = prev.find(
                (p) => p[valueKey] === newOpt[valueKey]
              );
              if (!prevOpt) return true;
              // Check if important fields changed (like code)
              return (
                prevOpt.name !== newOpt.name || prevOpt.code !== newOpt.code
              );
            });

            if (hasChanges) {
              // Merge new options into previous ones
              const newLocalOptions = [...prev];
              relevantOptions.forEach((newOpt) => {
                const idx = newLocalOptions.findIndex(
                  (p) => p[valueKey] === newOpt[valueKey]
                );
                if (idx >= 0) {
                  newLocalOptions[idx] = newOpt;
                } else {
                  newLocalOptions.push(newOpt);
                }
              });
              return newLocalOptions;
            }
            return prev;
          });
        }
      }
    },
    [onDataFetched, value, valueKey]
  );

  return (
    <SearchSelect
      debouncedInputValue={debouncedInputValue}
      inputValue={inputValue}
      setInputValue={setInputValue}
      localOptions={localOptions}
      setLocalOptions={safeSetLocalOptions}
      multiple={multiple}
      value={value}
      onChange={handleChange}
      minCharsToTrigerSearch={minCharsToTrigerSearch}
      valueKey={valueKey}
      noOptionHint="Aucun projet ne correspond"
      queryKey={[CacheInvalidationAction.GET_PROJECTS, filters]}
      queryFn={
        useRouteProjectWarehouse
          ? ProjectServices.getProjectsWithWarehouse
          : ProjectServices.getProjects
      }
      required={required}
      getOptionLabel={getProjectLabel}
      queryfilters={filters}
      onDataFetched={handleDataFetched}
      label={labelToUse}
      {...props}
    />
  );
};

export default ProjectSelect;
